import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {concat, interval, mergeMap, Observable, of, switchMap, take, takeUntil, withLatestFrom} from 'rxjs';
import {
  ChatDto,
  ChatMessageListDto,
  ChatMessageType,
  ContactChatMemberDto,
  LocationChatMemberDto,
  Orders
} from '@server-models';
import {catchError, map} from 'rxjs/operators';
import {SharedChatEffectsBase} from '@shared/components/chat/store/shared-chat.effects';
import {SharedIssueApiService} from '@shared/components/issues/services/shared-issue-api.service';
import {SharedChatActions} from '@shared/components/chat/store/shared-chat.actions-type';
import {SharedChatSelectors} from '@shared/components/chat/store/shared-chat.selectors-type';
import {SharedChatApiService} from '@shared/components/chat/services/shared-chat-api.service';

@Injectable({
  providedIn: 'root',
})
export class SharedChatApiEffects extends SharedChatEffectsBase {
  actionStream: Actions;
  override store: Store;
  protected _sharedIssueApiService = inject(SharedIssueApiService);
  private _sharedChatApiService = inject(SharedChatApiService);


  pollingIntervalMilliseconds = 30000;

  constructor() {
    const actionStream = inject(Actions);
    const store = inject(Store);

    super(store, actionStream);
  
    this.actionStream = actionStream;
    this.store = store;
  }

  startMessagePolling$ = createEffect(() =>
    this.actions().pipe(
      ofType(SharedChatActions.initMessagePolling),
      withLatestFrom(this.store.select(SharedChatSelectors.selectSharedChatStore)),
      switchMap(([, chatState]) => this.startPollingForChat(chatState.chat?.chatId ?? 0))
    )
  );

  private startPollingForChat(chatId: number): Observable<Action> {
    const initialMessagesRequest = this.loadInitialMessages(chatId);
    const newMessagesPolling = interval(this.pollingIntervalMilliseconds).pipe(
      switchMap(() => this.pollForNewMessages(chatId))
    );

    return concat(initialMessagesRequest, newMessagesPolling).pipe(
      takeUntil(this.actionStream.pipe(ofType(SharedChatActions.stopMessagePolling)))
    );
  }

  private loadInitialMessages(chatId: number): Observable<Action> {
    return this._sharedChatApiService.pollChatMessages(chatId, 0, Orders.Descending).pipe(
      map(response => SharedChatActions.messagePollingSuccess({ newItems: response.items })),
      catchError(() => of(SharedChatActions.messagePollingSuccess({ newItems: [] })))
    );
  }

  private pollForNewMessages(chatId: number): Observable<Action> {
    return this.store.select(SharedChatSelectors.selectSharedChatLastFetchedMessageId).pipe(
      take(1),
      switchMap(lastFetchedMessageId => this._sharedChatApiService.pollChatMessages(
        chatId,
        lastFetchedMessageId,
        Orders.Ascending
      )),
      map(response => SharedChatActions.messagePollingSuccess({ newItems: response.items })),
      catchError(() => of(SharedChatActions.messagePollingSuccess({ newItems: [] })))
    );
  }

  override initChat(action$: Observable<{ issueId: any }>): Observable<any> {
    return action$.pipe(mergeMap(({ issueId }) => this.requestInitialChat(issueId)));
  }

  private requestInitialChat(issueId: any) {
    return this._sharedIssueApiService.requestInitChat(issueId).pipe(
      mergeMap((data: any) => {
        return [SharedChatActions.initChatSuccess({ chat: data })];
      }),
      catchError((error) => [SharedChatActions.initChatFail({ error })])
    );
  }

  override initChatSuccess(action$: Observable<{ chat: ChatDto }>): Observable<any> {
    return action$.pipe(mergeMap(({ chat }) => this.requestChat(chat.chatId!)));
  }

  override getChat(action$: Observable<{ chatId: number }>): Observable<any> {
    return action$.pipe(mergeMap(({ chatId }) => this.requestChat(chatId)));
  }

  private requestChat(chatId: number) {
    return this._sharedChatApiService.getChatById(chatId).pipe(
      mergeMap((data: ChatDto) => {
        return [SharedChatActions.getChatSuccess({ chat: data })];
      }),
      catchError((error) => [SharedChatActions.getChatFail({ error })])
    );
  }

  override chatMessageSend(
    action$: Observable<{
      chatId: any;
      message: ChatMessageListDto;
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ chatId, message }) => this.sendMessageRequest(chatId, message))
    );
  }

  private sendMessageRequest(chatId: number, message: ChatMessageListDto): Observable<Action> {
    const { sender, ...messageWithoutSender } = message;

    return this._sharedChatApiService.addMessage(chatId, messageWithoutSender).pipe(
      map(savedMessage => SharedChatActions.chatMessageSendSuccess({
        newMessage: {
          ...savedMessage,
          sender: savedMessage.sender ?? sender,
          isMine: savedMessage.isMine ?? true,
          type: savedMessage.type ?? ChatMessageType.Message,
          time: savedMessage.time ?? new Date().toISOString()
        }
      })),
      catchError(error => of(SharedChatActions.chatMessageSendFail({ error })))
    );
  }

  override addChatMember(
    action$: Observable<{
      chatId: number;
      member: LocationChatMemberDto | ContactChatMemberDto;
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ chatId, member }) => {
        return this._sharedChatApiService.addChatMember(chatId, member).pipe(
          mergeMap((newMember) => {
            return [SharedChatActions.addChatMemberSuccess({ newMember })];
          }),
          catchError((error) => [SharedChatActions.addChatMemberFail({ error })])
        );
      })
    );
  }

  override removeChatMember(
    action$: Observable<{
      chatId: number;
      member: LocationChatMemberDto | ContactChatMemberDto;
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ chatId, member }) => {
        return this._sharedChatApiService.removeChatMember(chatId, member).pipe(
          mergeMap(() => [SharedChatActions.removeChatMemberSuccess({ removedMember: member })]),
          catchError((error) => [SharedChatActions.removeChatMemberFail({ error })])
        );
      })
    );
  }

  override addMe(
    action$: Observable<{
      chatId: number;
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ chatId }) => {
        return this._sharedChatApiService.addMe(chatId).pipe(
          mergeMap(() => [SharedChatActions.addMeSuccess()]),
          catchError((error) => [SharedChatActions.addMeFail({ error })])
        );
      })
    );
  }

  override removeMe(
    action$: Observable<{
      chatId: number;
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ chatId }) => {
        return this._sharedChatApiService.removeMe(chatId).pipe(
          mergeMap(() => [SharedChatActions.removeMeSuccess()]),
          catchError((error) => [SharedChatActions.removeMeFail({ error })])
        );
      })
    );
  }

  override deleteChatMessage(action$: Observable<{ chatId: number, chatMessageId: number }>): Observable<any> {
    return action$.pipe(
      mergeMap(({ chatId, chatMessageId }) => this._sharedChatApiService.deleteChatMessage(chatId, chatMessageId).pipe(
          mergeMap(() => [SharedChatActions.deleteChatMessageSuccess({ chatMessageId })]),
          catchError((error) => [SharedChatActions.deleteChatMessageFail({ error })])
        )
      )
    );
  }
}
